import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilTrash, cilX } from "@coreui/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/treemap";

import { apiClient } from "services/Api";
import { jwtDecode } from "jwt-decode";
import DataTableComponent from "components/DataTableComponent";
import AlertNotification from "components/globals/AlertNotification";
import TextFieldCell from "components/TextFieldCell";

const NonconformityList = () => {
  const navigate = useNavigate();
  const [nonconformities, setNonConformities] = useState([]);
  const location = useLocation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default to current year
  const [availableYears, setAvailableYears] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [distributions, setDistributions] = useState(null);
  const [previewNonconformity, setPreviewNonconformity] = useState(null);

  const token = localStorage.getItem("access_token");
  const decodedToken = jwtDecode(token);
  const is_director = decodedToken.is_director;
  const user_id = decodedToken.user_id;

  useEffect(() => {
    const fetchNonConformities = async (year = selectedYear) => {
      try {
        const response = await apiClient.get(`/nonconformities/?year=${year}`);
        setNonConformities(response.data.nonconformities);
        setPreviewNonconformity((current) =>
          current
            ? response.data.nonconformities.find(
                (nonconformity) => nonconformity.id === current.id,
              ) || null
            : null,
        );
        setAvailableYears(response.data.available_years);
        setKpis(response.data.kpis || null);
        setDistributions(response.data.distributions || null);
      } catch (error) {
        console.error("Error fetching nonconformities:", error);
      }
    };

    fetchNonConformities(selectedYear);
  }, [location.pathname, selectedYear]);

  const handleDelete = async (nonconformityId) => {
    try {
      await apiClient.delete(`/nonconformities/${nonconformityId}/`);
      setNonConformities((current) =>
        current.filter(
          (nonconformity) => nonconformity.id !== nonconformityId,
        ),
      );
      setPreviewNonconformity((current) =>
        current?.id === nonconformityId ? null : current,
      );
    } catch (error) {
      console.error("Error deleting nonconformities:", error);
    }
  };

  const columns = [
    {
      name: "Data",
      selector: (row) => row.created_at,
      sortable: true,
      className: "text-center",
    },
    {
      name: "Código",
      selector: (row) => row.code,
      sortable: true,
      className: "text-center",
    },
    {
      name: "Descrição",
      cell: (row) => (
        <TextFieldCell
          html={row.description}
          onClick={() => setPreviewNonconformity(row)}
        />
      ),
      sortable: true,
      grow: 5,
    },
  ];

  const buildPieOptions = (title, items) => ({
    chart: {
      type: "pie",
      height: 200,
      spacing: [4, 4, 4, 4],
      backgroundColor: "transparent",
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> ({point.percentage:.0f}%)",
    },
    plotOptions: {
      pie: {
        innerSize: "62%",
        borderWidth: 0,
        showInLegend: true,
        dataLabels: { enabled: false },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      symbolRadius: 4,
      symbolHeight: 10,
      symbolWidth: 10,
      itemStyle: { fontSize: "11px" },
    },
    series: [
      {
        name: title,
        colorByPoint: true,
        data: (items || []).map((item) => ({
          name: item.value,
          y: item.count,
        })),
      },
    ],
  });

  const buildTreemapOptions = (title, items) => ({
    chart: {
      height: 200,
      spacing: [6, 6, 6, 6],
      backgroundColor: "transparent",
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.value}</b>",
    },
    plotOptions: {
      treemap: {
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.7)",
        dataLabels: {
          enabled: true,
          format: "{point.name}",
          style: {
            textOutline: "none",
            fontSize: "10px",
          },
        },
      },
    },
    series: [
      {
        type: "treemap",
        layoutAlgorithm: "squarified",
        allowTraversingTree: false,
        colorByPoint: true,
        data: (items || []).map((item) => ({
          name: item.value,
          value: item.count,
        })),
      },
    ],
  });

  const renderDistribution = (title, items, chartType = "pie") => {
    const total = (items || []).reduce(
      (sum, item) => sum + (item.count || 0),
      0,
    );
    return (
      <CCard className=" h-100 shadow-sm">
        <CCardBody className="py-3">
          <div className="text-uppercase text-medium-emphasis small mb-1">
            {title}
          </div>
          {total === 0 ? (
            <div className="text-medium-emphasis small">
              Sem respostas no período.
            </div>
          ) : (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 220 }}
            >
              <HighchartsReact
                highcharts={Highcharts}
                options={
                  chartType === "treemap"
                    ? buildTreemapOptions(title, items)
                    : buildPieOptions(title, items)
                }
              />
            </div>
          )}
        </CCardBody>
      </CCard>
    );
  };

  return (
    <>
      <AlertNotification />
      <CRow className="g-3 mb-4">
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-total h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Ano
              </div>
              <div className="fs-4 fw-semibold">{selectedYear}</div>
              <div className="text-medium-emphasis small">
                Período selecionado
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-completion h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Nº de não conformidades
              </div>
              <div className="fs-4 fw-semibold">{kpis?.total ?? 0}</div>
              <div className="text-medium-emphasis small">Total registado</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-pending h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Com seguimento
              </div>
              <div className="fs-4 fw-semibold">
                {kpis?.follow_up_rate ?? 0}%
              </div>
              <div className="text-medium-emphasis small">
                {kpis?.follow_up_count ?? 0} casos
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

         <CRow className="g-3 mb-4">
            <CCol lg={4}>
               {renderDistribution("Origem", distributions?.origin)}
            </CCol>
        <CCol lg={4}>
          {renderDistribution("Estado", distributions?.approved)}
        </CCol>
        <CCol lg={4}>
          {renderDistribution("Corretiva", distributions?.corrective)}
        </CCol>
      </CRow>
      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Não Conformidades</h3>
          <Link to="/nao-conformidades/new">
            <CButton color="info" className="text-white">
              Nova N&atilde;o Conformidade
            </CButton>
          </Link>
        </CCardHeader>
        <CCardBody>
          <DataTableComponent
            columns={columns}
            data={nonconformities}
            years={availableYears} // Optional
            selectedYear={selectedYear} // Optional
            onYearChange={(newYear) => setSelectedYear(newYear)} // Optional
            onRowClicked={setPreviewNonconformity}
          />
        </CCardBody>
      </CCard>

      {previewNonconformity && (
        <div
          style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 1040 }}
          onClick={() => setPreviewNonconformity(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewNonconformity)}
        onHide={() => setPreviewNonconformity(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Não Conformidade</div>
              <div className="text-medium-emphasis small">
                {previewNonconformity?.code || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewNonconformity && (
              <CButton
                color="info"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/nao-conformidades/${previewNonconformity.id}`)}
              >
                <CIcon icon={cilPencil} className="me-1" />
                {previewNonconformity.approved == null &&
                (user_id === previewNonconformity.identified_by || is_director)
                  ? "Editar"
                  : "Ver"}
              </CButton>
            )}
            {previewNonconformity?.approved === null && is_director && (
              <CButton
                color="danger"
                size="sm"
                className="text-white"
                onClick={() => {
                  if (window.confirm("Tem a certeza que quer eliminar este registo?")) {
                    handleDelete(previewNonconformity.id);
                  }
                }}
              >
                <CIcon icon={cilTrash} className="me-1" />
                Eliminar
              </CButton>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewNonconformity(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewNonconformity && (
            <div className="d-flex flex-column gap-3">
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">Data</div>
                <div className="fw-semibold">{previewNonconformity.created_at || "-"}</div>
              </div>
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">Código</div>
                <div className="fw-semibold">{previewNonconformity.code || "-"}</div>
              </div>
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">Descrição</div>
                <TextFieldCell html={previewNonconformity.description || "-"} />
              </div>
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default NonconformityList;
